<template>
  <div class="pending__main">
    <bb-container>
      <bb-row justify="between">
        <h4 class="f-color-navy f-medium"> {{ translations.title }}</h4>
      </bb-row>
      <bb-row
        v-for="errorCode in errorCodes"
        :key="errorCode"
        class="m-t-10">
        <bb-banner
          :title="translations.errorTitles[errorCode]"
          type="error"
          visible
          permanent
          class="w-100"
        >
          <div class="f-color-gray">{{ translations.errorDescriptions[errorCode] }}</div>
          <bb-button
            v-if=showUpdateLimitsButton(errorCode)
            @click="onUpdateLimits"
            class="m-t-5"
            color="gray"
            size="sm"
            inverted>
            {{ translations.errorButtons[errorCode] }}
          </bb-button>
          <bb-button
            v-if=showIdentifyButton(errorCode)
            @click="onIdentify"
            class="m-t-5"
            color="gray"
            size="sm"
            inverted>
            {{ translations.errorButtons[errorCode] }}
          </bb-button>
        </bb-banner>
      </bb-row>
      <bb-row v-if="isMobile">
        <sign-pending-table-mobile
          class="m-t-10"
          :pending-payments="selectedPendings"
          :button-props="acceptButtonProps"
          @onSign="onSign"
        />
      </bb-row>
      <bb-row v-else>
        <sign-pending-table-desktop
          class="m-t-15"
          :pending-payments="selectedPendings"
          :button-props="acceptButtonProps"
          @onSign="onSign"
        />
      </bb-row>
    </bb-container>
    <sign-iframe
      v-if="signingModalVisible"
      :signing-request-id="signingRequestId"
      :redirect-url="redirectUrl"
      :complete-signing="completeSigning"
      @signingSuccessful="signingSuccessful"
      @signingError="signingError"
      @signingCancelled="signingCancelled"
      @close="onClose"/>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import { usePendingPaymentStore } from '@account/store/pendingPaymentStore'
import { useRootStore } from '@/store/root'
import SignPendingTableDesktop from '@account/views/payments/pending/SignPendingTableDesktop.vue'
import { AccountRouteName } from '@account/const'
import SignPendingTableMobile from '@account/views/payments/pending/SignPendingTableMobile.vue'
import api from '@account/api'
import { OrderStatus } from '@bigbank/dc-common/clients/http/account/modules/pending-payments/pending-payment.types'
import SignIframe from '@/components/signing/SignIframe.vue'

const VALIDATION_ERRORS = {
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  ACCOUNT_LIMIT_EXCEEDED: 'ACCOUNT_LIMIT_EXCEEDED',
  F2F_IDENT_MISSING_LIMIT_EXCEEDED: 'F2F_IDENT_MISSING_LIMIT_EXCEEDED',
  ARRESTED_ACCOUNT_PAYMENT_NOT_ALLOWED: 'ARRESTED_ACCOUNT_PAYMENT_NOT_ALLOWED',
  GENERIC_ERROR: 'GENERIC_ERROR'
}
const SPECIFIC_ERRORS = [
  VALIDATION_ERRORS.INSUFFICIENT_FUNDS,
  VALIDATION_ERRORS.ACCOUNT_LIMIT_EXCEEDED,
  VALIDATION_ERRORS.F2F_IDENT_MISSING_LIMIT_EXCEEDED,
  VALIDATION_ERRORS.ARRESTED_ACCOUNT_PAYMENT_NOT_ALLOWED
]

export default {
  components: {
    SignIframe,
    SignPendingTableMobile,
    SignPendingTableDesktop
  },
  data () {
    return {
      signingDisabled: false,
      signingLoading: false,
      signingModalVisible: false,
      signingRequestId: null,
      redirectUrl: null,
      errorCodes: []
    }
  },
  computed: {
    ...mapState(usePendingPaymentStore, ['pendingPayments']),
    ...mapState(useRootStore, ['isMobile', 'featureFlags']),
    selectedPendings () {
      const pendingPaymentIds = this.$route.query.pendingPaymentIds
      return this.pendingPayments.filter(p => pendingPaymentIds.includes(p.id))
    },
    translations () {
      return {
        title: this.$pgettext('pending_payment_sign', 'Summary'),
        errorTitles: {
          [VALIDATION_ERRORS.INSUFFICIENT_FUNDS]: this.$pgettext('pending_payment_sign', 'Account does not have enough available balance'),
          [VALIDATION_ERRORS.ACCOUNT_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'Account limit(s) exceeded'),
          [VALIDATION_ERRORS.F2F_IDENT_MISSING_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'Monthly payout limit of €15,000 exceeded'),
          [VALIDATION_ERRORS.ARRESTED_ACCOUNT_PAYMENT_NOT_ALLOWED]: this.$pgettext('pending_payment_sign', 'Arrested account payment not allowed'),
          [VALIDATION_ERRORS.GENERIC_ERROR]: this.$pgettext('pending_payment_sign', 'An error occurred, please try again later')
        },
        errorDescriptions: {
          [VALIDATION_ERRORS.INSUFFICIENT_FUNDS]: this.$pgettext('pending_payment_sign', 'Please check your account balance and try again.'),
          [VALIDATION_ERRORS.ACCOUNT_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'Please check your account limits and try again.'),
          [VALIDATION_ERRORS.F2F_IDENT_MISSING_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'In order to make payments you need to identify yourself'),
          [VALIDATION_ERRORS.ARRESTED_ACCOUNT_PAYMENT_NOT_ALLOWED]: this.$pgettext('pending_payment_sign', 'Arrested account payments are not allowed. Please contact customer support for more information.'),
          [VALIDATION_ERRORS.GENERIC_ERROR]: this.$pgettext('pending_payment_sign', 'An unexpected error occurred, please try again later.')
        },
        errorButtons: {
          [VALIDATION_ERRORS.ACCOUNT_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'Update limits'),
          [VALIDATION_ERRORS.F2F_IDENT_MISSING_LIMIT_EXCEEDED]: this.$pgettext('pending_payment_sign', 'Identify')
        },
        notification: {
          title: this.$pgettext('pending_payment_sign', 'PENDING payment was approved'),
          text: this.$pgettext('pending_payment_sign', 'Payment is being processed')
        }
      }
    },
    acceptButtonProps () {
      return {
        disabled: this.signingDisabled || this.signingLoading,
        loading: this.signingLoading
      }
    }
  },
  methods: {
    ...mapActions(usePendingPaymentStore, ['reloadPendingPayments']),
    async completeSigning (signingRequestId) {
      try {
        return await api.completePendingSigning(signingRequestId)
      } catch (error) {
        if (error.data?.errors) {
          this.handleErrors(error.data.errors)
          this.signingModalVisible = false
        } else if (error?.err_code) {
          this.handleErrors([{ code: error.err_code }])
        }
        this.signingDisabled = true
        this.signingLoading = false
      }
    },
    async onSign () {
      this.signingLoading = true

      try {
        const pendingPaymentIds = this.$route.query.pendingPaymentIds
        // Not confirmed payments are executed in the future
        // Confirmed payments are executed immediately
        const future = this.pendingPayments.some(p => p.orderStatus === OrderStatus.NotConfirmed)
        const response = await api.initPendingSigning({
          pendingPaymentIds,
          future
        })
        this.signingRequestId = response.signingRequestId
        this.redirectUrl = response.redirectUrl
        this.signingModalVisible = true
      } catch (error) {
        if (error.data?.errors) {
          this.handleErrors(error.data.errors)
        } else if (error?.err_code) {
          this.handleErrors([{ code: error.err_code }])
        }
        this.signingDisabled = true
        this.signingLoading = false
      }
    },
    onClose () {
      this.signingModalVisible = false
      this.signingLoading = false
    },
    signingSuccessful () {
      this.reloadPendingPayments()
      this.$notify({
        type: 'success',
        duration: 5000,
        text: this.translations.notification.text,
        title: this.translations.notification.title
      })
      this.$router.push({ name: AccountRouteName.PendingPayments })
    },
    signingError () {
      this.signingLoading = false
      this.signingModalVisible = false
    },
    signingCancelled () {
      this.signingLoading = false
      this.$router.push({ name: AccountRouteName.PendingPayments })
    },
    handleErrors (errors) {
      this.errorCodes = [...new Set(errors.map((error) => SPECIFIC_ERRORS.includes(error.code) ? error.code : VALIDATION_ERRORS.GENERIC_ERROR))]
    },
    showUpdateLimitsButton (errorCode) {
      return errorCode === VALIDATION_ERRORS.ACCOUNT_LIMIT_EXCEEDED && this.featureFlags.enableAccountsLimitsTab
    },
    showIdentifyButton (errorCode) {
      return errorCode === VALIDATION_ERRORS.F2F_IDENT_MISSING_LIMIT_EXCEEDED && this.featureFlags.enableUnverifiedCustomerAdditionalLimits
    },
    onUpdateLimits () {
      this.$router.push({ name: AccountRouteName.AccountsLimits })
    },
    onIdentify () {
      this.$router.push({ name: AccountRouteName.AccountVerification })
    }
  },
  async mounted () {
    if (this.pendingPayments.length === 0) {
      // Refreshing page will go back to pending list
      this.$router.push({ name: AccountRouteName.PendingPayments })
    }
  }
}
</script>

<style lang="scss" scoped>
.pending {
  &__main {
    margin-top: 40px;
    padding-left: 15px;
    padding-right: 15px;

    @media (max-width: $mobile-view-breaking-point) {
      margin-top: 15px;
    }
  }
}
</style>
